import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Star } from "lucide-react";
import { BrandedImage } from "@/components/images/BrandedImage";
interface ProfessionalProfilesRowProps {
  city: string;
}

export const ProfessionalProfilesRow = ({ city }: ProfessionalProfilesRowProps) => {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        // Trainer: try same city first, then anywhere
        const trainerCity = await supabase
          .from('trainer_profiles_public')
          .select('id, full_name, slug, location_city, profile_image')
          .eq('is_active', true)
          .ilike('location_city', city)
          .limit(1)
          .maybeSingle();
        const trainer = trainerCity.data ? trainerCity.data : (await supabase
          .from('trainer_profiles_public')
          .select('id, full_name, slug, location_city, profile_image')
          .eq('is_active', true)
          .limit(1)
          .maybeSingle()).data;

        // Nutritionist
        const nutritionistCity = await supabase
          .from('nutritionist_profiles')
          .select('id, full_name, slug, location_city, profile_image')
          .eq('is_active', true)
          .ilike('location_city', city)
          .limit(1)
          .maybeSingle();
        const nutritionist = nutritionistCity.data ? nutritionistCity.data : (await supabase
          .from('nutritionist_profiles')
          .select('id, full_name, slug, location_city, profile_image')
          .eq('is_active', true)
          .limit(1)
          .maybeSingle()).data;

        // Physiotherapist
        const physioCity = await supabase
          .from('physiotherapist_profiles')
          .select('id, full_name, slug, location_city, profile_image')
          .eq('is_active', true)
          .ilike('location_city', city)
          .limit(1)
          .maybeSingle();
        const physio = physioCity.data ? physioCity.data : (await supabase
          .from('physiotherapist_profiles')
          .select('id, full_name, slug, location_city, profile_image')
          .eq('is_active', true)
          .limit(1)
          .maybeSingle()).data;

        // Ensure uniqueness and fixed order
        const seen = new Set<string>();
        const make = (item: any, type: string, url: string, icon: string) => {
          if (!item) return null;
          if (seen.has(item.id)) return null;
          seen.add(item.id);
          return {
            id: item.id,
            full_name: item.full_name,
            slug: item.slug,
            location_city: item.location_city,
            profile_image: item.profile_image,
            rating: 0,
            type,
            url,
            icon,
          };
        };

        const combined = [
          make(trainer, 'Entrenador Personal', '/trainer', '💪'),
          make(nutritionist, 'Nutricionista', '/nutricionista', '🥗'),
          make(physio, 'Fisioterapeuta', '/fisioterapeuta', '💆'),
        ].filter(Boolean) as any[];

        setProfiles(combined);
      } catch (e) {
        console.error('Error fetching combined profiles:', e);
        setProfiles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProfiles();
  }, [city]);

  if (loading) return null;

  if (profiles.length === 0) return null;

  return (
    <Card>
      <div className="bg-gradient-to-r from-muted/50 to-muted/30 p-6 border-b">
        <h2 className="text-2xl font-bold">Profesionales en {city}</h2>
      </div>
      <CardContent className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {profiles.map((profile) => (
            <div
              key={profile.id}
              onClick={() => navigate(`${profile.url}/${profile.slug}`)}
              className="flex flex-col items-center p-4 rounded-lg border hover:border-primary/50 hover:bg-muted/50 transition-all cursor-pointer"
            >
              <BrandedImage
                src={profile.profile_image}
                category={profile.type.toLowerCase().replace(' personal', '')}
                alt={profile.full_name}
                aspectRatio="aspect-square"
                containerClassName="w-24 h-24 rounded-full overflow-hidden mb-3"
                logoSize="sm"
                logoPosition="bottom-right"
              />
              <h3 className="font-semibold text-center mb-1">{profile.full_name}</h3>
              <Badge variant="secondary" className="text-xs mb-2">
                {profile.icon} {profile.type}
              </Badge>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <MapPin className="w-3 h-3" />
                <span>{profile.location_city}</span>
              </div>
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 fill-primary text-primary" />
                <span className="text-sm font-semibold">{profile.rating || 0}</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};